import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { apiFetch } from "../api/client";
import { tenantPath } from "../utils/tenantContext";

const REFRESH_INTERVAL_MS = 30000;

const normalizeFinding = (finding, index) => ({
  id:
    finding.finding_uuid ||
    finding.id ||
    `${finding.device_uuid || "device"}-${index}`,

  detected:
    finding.detected ||
    finding.detected_at ||
    finding.created_at ||
    finding.updated_at ||
    null,

  device:
    finding.device ||
    finding.hostname ||
    finding.device_name ||
    finding.device_uuid ||
    "Unknown device",

  deviceUuid:
    finding.device_uuid ||
    null,

  application:
    finding.application ||
    finding.app_name ||
    finding.software_name ||
    "Unknown application",

  version:
    finding.version ||
    finding.app_version ||
    finding.software_version ||
    "Unknown",

  cve:
    finding.cve ||
    finding.cve_id ||
    "No CVE assigned",

  cvss:
    finding.cvss ??
    finding.cvss_score ??
    null,

  severity:
    finding.severity ||
    "Monitoring",

  summary:
    finding.summary ||
    "No technical summary is currently available.",

  exposureType:
    finding.exposure_type ||
    finding.exposure ||
    "Software Inventory",

  recommendation:
    finding.recommendation ||
    "Continue monitoring this application and confirm it remains patched and authorized.",

  aiScore:
    finding.ai_score ??
    finding.risk_score ??
    null,

  framework:
    finding.framework ||
    finding.attack_framework ||
    finding.mitre_attack ||
    finding.attack_mapping ||
    null,

  kev:
    finding.kev_known_exploited ??
    finding.known_exploited ??
    false,
});

const severityRank = {
  critical: 5,
  high: 4,
  medium: 3,
  moderate: 3,
  low: 2,
  monitoring: 1,
  informational: 1,
  info: 1,
};

const severityClass = (severity) => {
  const value = String(severity || "").toLowerCase();

  if (value === "critical") return "ai-severity critical";
  if (value === "high") return "ai-severity high";
  if (value === "medium" || value === "moderate") {
    return "ai-severity medium";
  }
  if (value === "low") return "ai-severity low";

  return "ai-severity monitoring";
};

const formatDate = (value) => {
  if (!value) return "Not available";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString();
};

function AIDiscovery() {
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);
  const [selectedFinding, setSelectedFinding] = useState(null);

  const loadDiscovery = useCallback(
    async (silent = false) => {
      try {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        setError("");

        const response = await apiFetch(
          tenantPath("/vulnerabilities/")
        );

        let data = null;

        try {
          data = await response.json();
        } catch {
          data = null;
        }

        if (!response.ok) {
          throw new Error(
            data?.detail ||
              data?.message ||
              `AI Discovery request failed with HTTP ${response.status}.`
          );
        }

        const source = Array.isArray(data)
          ? data
          : Array.isArray(data?.findings)
          ? data.findings
          : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.results)
          ? data.results
          : [];

        const normalized = source.map(normalizeFinding);

        setFindings(normalized);
        setLastRefresh(new Date());

        setSelectedFinding((current) => {
          if (!current) {
            return normalized[0] || null;
          }

          return (
            normalized.find(
              (finding) => finding.id === current.id
            ) ||
            normalized[0] ||
            null
          );
        });
      } catch (loadError) {
        console.error(
          "AI Discovery load failed:",
          loadError
        );

        setError(
          loadError.message ||
            "Unable to load AI Discovery data."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    loadDiscovery(false);

    const timer = window.setInterval(() => {
      loadDiscovery(true);
    }, REFRESH_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [loadDiscovery]);

  const metrics = useMemo(() => {
    const critical = findings.filter(
      (finding) =>
        String(finding.severity).toLowerCase() === "critical"
    ).length;

    const high = findings.filter(
      (finding) =>
        String(finding.severity).toLowerCase() === "high"
    ).length;

    const kev = findings.filter(
      (finding) => Boolean(finding.kev)
    ).length;

    const devices = new Set(
      findings
        .map((finding) => finding.deviceUuid || finding.device)
        .filter(Boolean)
    ).size;

    return {
      total: findings.length,
      critical,
      high,
      kev,
      devices,
    };
  }, [findings]);

  const prioritizedFindings = useMemo(
    () =>
      [...findings].sort((left, right) => {
        const leftRank =
          severityRank[
            String(left.severity || "").toLowerCase()
          ] || 0;

        const rightRank =
          severityRank[
            String(right.severity || "").toLowerCase()
          ] || 0;

        if (rightRank !== leftRank) {
          return rightRank - leftRank;
        }

        return (
          Number(right.aiScore || right.cvss || 0) -
          Number(left.aiScore || left.cvss || 0)
        );
      }),
    [findings]
  );

  return (
    <div className="ai-discovery-page">
      <style>{`
        .ai-discovery-page {
          min-height: 100%;
          color: #e8edf7;
        }

        .ai-discovery-page * {
          box-sizing: border-box;
        }

        .ai-discovery-hero {
          display: flex;
          justify-content: space-between;
          gap: 24px;
          align-items: flex-start;
          padding: 26px;
          border: 1px solid rgba(93, 140, 255, .22);
          border-radius: 18px;
          background:
            radial-gradient(circle at top right,
              rgba(61, 104, 255, .16),
              transparent 38%),
            linear-gradient(145deg,
              rgba(17, 28, 52, .96),
              rgba(9, 17, 34, .98));
          box-shadow: 0 20px 50px rgba(0, 0, 0, .22);
        }

        .ai-discovery-eyebrow {
          font-size: 12px;
          font-weight: 800;
          letter-spacing: .16em;
          color: #6fa8ff;
          margin-bottom: 8px;
        }

        .ai-discovery-hero h2 {
          margin: 0;
          font-size: 30px;
          color: #ffffff;
        }

        .ai-discovery-hero p {
          margin: 10px 0 0;
          max-width: 780px;
          color: #9eabc1;
          line-height: 1.65;
        }

        .ai-refresh {
          min-width: 148px;
          border: 1px solid #487cff;
          border-radius: 10px;
          background: #1d4ed8;
          color: #fff;
          padding: 11px 16px;
          font-weight: 700;
          cursor: pointer;
        }

        .ai-refresh:disabled {
          cursor: default;
          opacity: .6;
        }

        .ai-operation {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 12px;
          margin: 18px 0;
        }

        .ai-operation article,
        .ai-metric {
          border: 1px solid rgba(120, 145, 190, .18);
          border-radius: 14px;
          background: rgba(13, 23, 43, .92);
          padding: 17px;
        }

        .ai-operation strong {
          display: block;
          margin-bottom: 6px;
          color: #dce7ff;
        }

        .ai-operation span {
          font-size: 13px;
          color: #8491a8;
        }

        .ai-operation-number {
          width: 28px;
          height: 28px;
          display: grid;
          place-items: center;
          border-radius: 50%;
          margin-bottom: 12px;
          background: rgba(52, 105, 255, .18);
          color: #76a3ff;
          font-weight: 800;
        }

        .ai-metrics {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 12px;
          margin-bottom: 18px;
        }

        .ai-metric span {
          display: block;
          font-size: 12px;
          letter-spacing: .08em;
          text-transform: uppercase;
          color: #78879f;
        }

        .ai-metric strong {
          display: block;
          margin-top: 7px;
          font-size: 25px;
          color: #fff;
        }

        .ai-warning {
          margin-bottom: 18px;
          padding: 14px 16px;
          border: 1px solid rgba(255, 153, 80, .35);
          border-radius: 12px;
          background: rgba(117, 53, 16, .18);
          color: #ffc18c;
        }

        .ai-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.65fr) minmax(330px, .75fr);
          gap: 18px;
        }

        .ai-panel {
          overflow: hidden;
          border: 1px solid rgba(120, 145, 190, .18);
          border-radius: 16px;
          background: rgba(11, 20, 38, .96);
        }

        .ai-panel-header {
          padding: 18px 20px;
          border-bottom: 1px solid rgba(120, 145, 190, .14);
        }

        .ai-panel-header h3 {
          margin: 0;
          color: #fff;
        }

        .ai-panel-header p {
          margin: 6px 0 0;
          font-size: 13px;
          color: #7f8da4;
        }

        .ai-table-wrap {
          overflow-x: auto;
        }

        .ai-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 850px;
        }

        .ai-table th,
        .ai-table td {
          padding: 13px 15px;
          text-align: left;
          border-bottom: 1px solid rgba(120, 145, 190, .10);
          vertical-align: top;
        }

        .ai-table th {
          font-size: 11px;
          letter-spacing: .08em;
          text-transform: uppercase;
          color: #718199;
          background: rgba(17, 28, 49, .72);
        }

        .ai-table td {
          font-size: 13px;
          color: #c7d1e3;
        }

        .ai-row {
          cursor: pointer;
        }

        .ai-row:hover,
        .ai-row.selected {
          background: rgba(58, 105, 218, .10);
        }

        .ai-cell-sub {
          display: block;
          margin-top: 4px;
          font-size: 11px;
          color: #6e7c92;
        }

        .ai-severity {
          display: inline-flex;
          border-radius: 999px;
          padding: 4px 9px;
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .06em;
        }

        .ai-severity.critical {
          background: rgba(218, 53, 69, .18);
          color: #ff7885;
        }

        .ai-severity.high {
          background: rgba(232, 116, 30, .17);
          color: #ffad6d;
        }

        .ai-severity.medium {
          background: rgba(214, 165, 28, .16);
          color: #ffd86d;
        }

        .ai-severity.low {
          background: rgba(44, 156, 103, .15);
          color: #79dda9;
        }

        .ai-severity.monitoring {
          background: rgba(73, 123, 212, .16);
          color: #8eb5ff;
        }

        .ai-detail {
          padding: 20px;
        }

        .ai-detail-block {
          padding: 14px 0;
          border-bottom: 1px solid rgba(120, 145, 190, .10);
        }

        .ai-detail-block:last-child {
          border-bottom: 0;
        }

        .ai-detail-label {
          display: block;
          margin-bottom: 6px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: .1em;
          color: #718199;
        }

        .ai-detail-value {
          color: #dce5f4;
          line-height: 1.55;
        }

        .ai-evidence-note {
          margin-top: 14px;
          padding: 12px;
          border-radius: 10px;
          background: rgba(49, 89, 171, .11);
          color: #87a6df;
          font-size: 12px;
          line-height: 1.55;
        }

        .ai-empty {
          padding: 42px 24px;
          text-align: center;
          color: #76859b;
        }

        .ai-footer-note {
          margin-top: 16px;
          font-size: 12px;
          color: #65758d;
        }

        @media (max-width: 1100px) {
          .ai-metrics {
            grid-template-columns: repeat(3, 1fr);
          }

          .ai-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 760px) {
          .ai-discovery-hero {
            flex-direction: column;
          }

          .ai-operation,
          .ai-metrics {
            grid-template-columns: 1fr 1fr;
          }
        }
      `}</style>

      <section className="ai-discovery-hero">
        <div>
          <div className="ai-discovery-eyebrow">
            CONGO MARKET AI
          </div>

          <h2>AI Discovery</h2>

          <p>
            Continuously surface installed-software exposure,
            prioritize risk, and translate available evidence into
            actionable security recommendations without fabricating
            unsupported findings.
          </p>
        </div>

        <button
          type="button"
          className="ai-refresh"
          onClick={() => loadDiscovery(true)}
          disabled={refreshing}
        >
          {refreshing ? "Refreshing..." : "Refresh Discovery"}
        </button>
      </section>

      <section className="ai-operation">
        <article>
          <div className="ai-operation-number">1</div>
          <strong>Discover</strong>
          <span>
            Read tenant-scoped software and vulnerability evidence.
          </span>
        </article>

        <article>
          <div className="ai-operation-number">2</div>
          <strong>Prioritize</strong>
          <span>
            Order exposures by severity and available risk evidence.
          </span>
        </article>

        <article>
          <div className="ai-operation-number">3</div>
          <strong>Map</strong>
          <span>
            Display framework mapping only when supplied by evidence.
          </span>
        </article>

        <article>
          <div className="ai-operation-number">4</div>
          <strong>Translate</strong>
          <span>
            Present technical findings and remediation guidance.
          </span>
        </article>
      </section>

      <section className="ai-metrics">
        <article className="ai-metric">
          <span>Total Exposures</span>
          <strong>{metrics.total}</strong>
        </article>

        <article className="ai-metric">
          <span>Critical</span>
          <strong>{metrics.critical}</strong>
        </article>

        <article className="ai-metric">
          <span>High</span>
          <strong>{metrics.high}</strong>
        </article>

        <article className="ai-metric">
          <span>Known Exploited</span>
          <strong>{metrics.kev}</strong>
        </article>

        <article className="ai-metric">
          <span>Devices Represented</span>
          <strong>{metrics.devices}</strong>
        </article>
      </section>

      {error && (
        <div className="ai-warning">
          AI Discovery could not refresh: {error}
        </div>
      )}

      <section className="ai-grid">
        <div className="ai-panel">
          <div className="ai-panel-header">
            <h3>Live Exposure Inventory</h3>

            <p>
              {lastRefresh
                ? `Last refreshed ${lastRefresh.toLocaleTimeString()}`
                : "Waiting for discovery data"}
              {" · "}
              automatic refresh every 30 seconds
            </p>
          </div>

          {loading ? (
            <div className="ai-empty">
              Loading AI Discovery evidence...
            </div>
          ) : prioritizedFindings.length > 0 ? (
            <div className="ai-table-wrap">
              <table className="ai-table">
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Application</th>
                    <th>CVE</th>
                    <th>Severity</th>
                    <th>CVSS / AI</th>
                    <th>Exposure</th>
                  </tr>
                </thead>

                <tbody>
                  {prioritizedFindings.map((finding) => (
                    <tr
                      key={finding.id}
                      className={
                        selectedFinding?.id === finding.id
                          ? "ai-row selected"
                          : "ai-row"
                      }
                      onClick={() =>
                        setSelectedFinding(finding)
                      }
                    >
                      <td>
                        {finding.device}

                        {finding.deviceUuid && (
                          <span className="ai-cell-sub">
                            {finding.deviceUuid}
                          </span>
                        )}
                      </td>

                      <td>
                        {finding.application}
                        <span className="ai-cell-sub">
                          Version {finding.version}
                        </span>
                      </td>

                      <td>{finding.cve}</td>

                      <td>
                        <span
                          className={severityClass(
                            finding.severity
                          )}
                        >
                          {finding.severity}
                        </span>
                      </td>

                      <td>
                        {finding.cvss !== null
                          ? `CVSS ${finding.cvss}`
                          : "CVSS unavailable"}

                        <span className="ai-cell-sub">
                          {finding.aiScore !== null
                            ? `AI score ${finding.aiScore}`
                            : "AI score unavailable"}
                        </span>
                      </td>

                      <td>
                        {finding.exposureType}

                        {finding.kev && (
                          <span className="ai-cell-sub">
                            Known Exploited evidence present
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="ai-empty">
              No tenant-scoped AI Discovery findings are currently
              available.
            </div>
          )}
        </div>

        <aside className="ai-panel">
          <div className="ai-panel-header">
            <h3>Discovery Analysis</h3>
            <p>
              Evidence-backed technical and business-facing context.
            </p>
          </div>

          {selectedFinding ? (
            <div className="ai-detail">
              <div className="ai-detail-block">
                <span className="ai-detail-label">
                  Finding
                </span>

                <div className="ai-detail-value">
                  <strong>
                    {selectedFinding.application}
                  </strong>
                  {" "}
                  {selectedFinding.version}
                </div>
              </div>

              <div className="ai-detail-block">
                <span className="ai-detail-label">
                  Technical Summary
                </span>

                <div className="ai-detail-value">
                  {selectedFinding.summary}
                </div>
              </div>

              <div className="ai-detail-block">
                <span className="ai-detail-label">
                  Recommended Action
                </span>

                <div className="ai-detail-value">
                  {selectedFinding.recommendation}
                </div>
              </div>

              <div className="ai-detail-block">
                <span className="ai-detail-label">
                  Framework Mapping
                </span>

                <div className="ai-detail-value">
                  {selectedFinding.framework ||
                    "Not mapped by current evidence"}
                </div>
              </div>

              <div className="ai-detail-block">
                <span className="ai-detail-label">
                  Detected
                </span>

                <div className="ai-detail-value">
                  {formatDate(selectedFinding.detected)}
                </div>
              </div>

              <div className="ai-evidence-note">
                AI Discovery displays tenant-scoped evidence returned
                by the security platform. Missing CVE, framework, or
                scoring evidence is shown as unavailable rather than
                inferred as fact.
              </div>
            </div>
          ) : (
            <div className="ai-empty">
              Select a finding to review its discovery analysis.
            </div>
          )}
        </aside>
      </section>

      <div className="ai-footer-note">
        Discovery is read-only from this page. Remediation and
        controlled response actions remain in their authorized
        platform workflows.
      </div>
    </div>
  );
}

export default AIDiscovery;
